import {ErrorHandler, Inject, Injectable, Injector} from '@angular/core';
import {FirebaseApp} from '@angular/fire/app';
import {getAuth} from '@angular/fire/auth';
import {getFunctions, httpsCallable} from '@angular/fire/functions';
import {ActivatedRouteSnapshot, NavigationEnd, Router} from '@angular/router';
import {filter} from 'rxjs/operators';
import {environment} from 'src/environments/environment';

interface ClientBreadcrumb {
  category: string;
  message: string;
  timestamp: string;
}

interface ClientErrorReport {
  name: string;
  message: string;
  stack: string;
  operation: string;
  page: string;
  route: string;
  version: string;
  environment: string;
  capturedAt: string;
  breadcrumbs: ClientBreadcrumb[];
  device: {
    userAgent: string;
    platform: string;
    language: string;
    viewport: string;
    screen: string;
    pixelRatio: number;
    online: boolean;
    standalone: boolean;
    timezone: string;
  };
}

@Injectable({providedIn: 'root'})
export class ClientErrorReporterService {
  private readonly breadcrumbs: ClientBreadcrumb[] = [];
  private readonly reportClientErrorFn: (
    data: ClientErrorReport
  ) => Promise<{data: {success: boolean}}>;
  private initialized = false;
  private lastFingerprint = '';
  private lastReportedAt = 0;
  private reportsThisSession = 0;

  constructor(
    @Inject(FirebaseApp) private app: FirebaseApp,
    private router: Router,
  ) {
    this.reportClientErrorFn = httpsCallable<ClientErrorReport, {success: boolean}>(
      getFunctions(this.app, 'europe-west1'),
      'reportClientError'
    );
  }

  initialize(): void {
    if (!environment.production || this.initialized) {
      return;
    }
    this.initialized = true;

    this.addBreadcrumb('app', `Started APG ${environment.appVersion}`);
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => this.addBreadcrumb('navigation', this.pageWithoutQuery(event.urlAfterRedirects)));

    document.addEventListener('click', event => {
      const target = event.target instanceof Element
        ? event.target.closest('[data-error-breadcrumb], button, a, [role="button"]')
        : null;
      if (!target) {
        return;
      }
      const label = target.getAttribute('data-error-breadcrumb') || target.id || target.tagName.toLowerCase();
      this.addBreadcrumb('ui', label);
    }, true);

    window.addEventListener('error', event => {
      this.capture(event.error || event.message, 'window.error');
    });
    window.addEventListener('unhandledrejection', event => {
      this.capture(event.reason, 'unhandledrejection');
    });
  }

  addBreadcrumb(category: string, message: string): void {
    if (!environment.production || !message) {
      return;
    }
    this.breadcrumbs.push({
      category: category.slice(0, 40),
      message: message.slice(0, 200),
      timestamp: new Date().toISOString(),
    });
    if (this.breadcrumbs.length > 20) {
      this.breadcrumbs.shift();
    }
  }

  capture(error: unknown, operation = ''): void {
    if (!environment.production || !getAuth(this.app).currentUser || this.reportsThisSession >= 10) {
      return;
    }

    const normalized = this.normalizeError(error);
    if (normalized.name === 'UnknownError' && normalized.message === 'setPhotoOptions failed') {
      return;
    }
    const fingerprint = `${normalized.name}|${normalized.message}|${normalized.stack.slice(0, 500)}`;
    const now = Date.now();
    if (fingerprint === this.lastFingerprint && now - this.lastReportedAt < 30000) {
      return;
    }
    this.lastFingerprint = fingerprint;
    this.lastReportedAt = now;
    this.reportsThisSession++;

    const report: ClientErrorReport = {
      ...normalized,
      operation: operation.slice(0, 120),
      page: this.pageWithoutQuery(this.router.url || window.location.hash),
      route: this.routeTemplate(),
      version: environment.appVersion,
      environment: environment.name,
      capturedAt: new Date().toISOString(),
      breadcrumbs: [...this.breadcrumbs],
      device: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        screen: `${window.screen.width}x${window.screen.height}`,
        pixelRatio: window.devicePixelRatio,
        online: navigator.onLine,
        standalone: window.matchMedia('(display-mode: standalone)').matches,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    };

    this.reportClientErrorFn(report).catch(reportingError => {
      console.warn('Could not report client error:', reportingError);
    });
  }

  private normalizeError(error: unknown): Pick<ClientErrorReport, 'name' | 'message' | 'stack'> {
    const wrapped = error as {ngOriginalError?: unknown};
    const original = wrapped?.ngOriginalError || error;
    if (original instanceof Error) {
      return {
        name: original.name || 'Error',
        message: original.message || String(original),
        stack: original.stack || '',
      };
    }
    if (original && typeof original === 'object') {
      const value = original as {name?: unknown; message?: unknown; stack?: unknown};
      return {
        name: typeof value.name === 'string' ? value.name : 'Error',
        message: typeof value.message === 'string' ? value.message : String(original),
        stack: typeof value.stack === 'string' ? value.stack : '',
      };
    }
    return {name: 'Error', message: String(original), stack: ''};
  }

  private routeTemplate(): string {
    const parts: string[] = [];
    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    while (route) {
      if (route.routeConfig?.path) {
        parts.push(route.routeConfig.path);
      }
      route = route.firstChild;
    }
    return `/${parts.join('/')}`;
  }

  private pageWithoutQuery(page: string): string {
    return page.split('?')[0].slice(0, 1000);
  }
}

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  constructor(private injector: Injector) {}

  handleError(error: unknown): void {
    console.error(error);
    this.injector.get(ClientErrorReporterService).capture(error, 'angular');
  }
}
